import React from 'react';
import { BellRinging, X } from '@phosphor-icons/react';
import { FilterSwitch } from '../../design-system/FilterSwitch';
import { Link } from '../Link';

export interface AlertCtaBannerProps {
  ariaLabel: string;
  alreadyActive: boolean;
  pendingConfirmation: boolean;
  isAuthenticated: boolean;
  busy: boolean;
  failed: boolean;
  uiLocale: 'en' | 'fi' | 'sv';
  bannerText: React.ReactNode;
  /** Small supporting line under bannerText in the default (off) state only: states the
   * concrete mechanics (frequency, free, cancel anytime) so the CTA sells the actual benefit
   * instead of just naming the feature. Omitted once active/pending, which carry their own copy. */
  subText?: React.ReactNode;
  successText: React.ReactNode;
  manageLinkLabel: string;
  pendingText: React.ReactNode;
  /** Accessible label for the on/off switch itself (e.g. "Get email alerts"). */
  toggleLabel: string;
  dismissLabel: string;
  errorText: React.ReactNode;
  /** Optional secondary content rendered as a divided footer row inside the same card, below
   * the main CTA (e.g. JobDetailAlertCta folds the page's sign-in/feedback links in here so the
   * job-detail page reads as one card with one primary button instead of two stacked CTA
   * blocks). */
  footer?: React.ReactNode;
  onDismiss: () => void;
  onAuthenticatedClick: () => void;
  onContinueAsGuest: () => void;
  /** Company's real brand colour (job-detail page only); undefined keeps the shared generic jc-accent green (SRP). */
  accentColor?: string;
}

/**
 * Shared visual chrome behind SrpAlertCta and JobDetailAlertCta (TEC-514 redesign, third pass):
 * both previously duplicated the same JSX with the state machine already unified via
 * useAlertCtaCore, so extracting the presentation too removes the last copy-paste risk. Each
 * caller still owns its own defineMessages/copy (role/location interpolation differs between a
 * live SRP query and a synthetic job-facet search) and passes already-formatted nodes in.
 *
 * A CTA *button* that swapped the whole card's content on click (a headline+button collapsed
 * state that click-expanded into a differently-shaped paragraph+checkbox+button consent state)
 * read as if a second, unrelated card had appeared in its place. Replaced with a single on/off
 * switch: the card's copy and layout never change shape. Toggling it on, for a signed-out user,
 * goes straight to sign-in, no click-to-expand step first.
 *
 * The marketing-consent checkbox that used to live in that expand step is deliberately not here
 * right now: asking someone to decide two things (get alerts, AND opt into marketing email) in
 * the same single click was one ask too many for this exact moment. Get the account-for-alerts
 * conversion working first; marketing opt-in gets its own touchpoint later (TEC-514 workstream 2,
 * the digest-email cross-promo, still does this).
 */
export function AlertCtaBanner({
  ariaLabel,
  alreadyActive,
  pendingConfirmation,
  isAuthenticated,
  busy,
  failed,
  uiLocale,
  bannerText,
  subText,
  successText,
  manageLinkLabel,
  pendingText,
  toggleLabel,
  dismissLabel,
  errorText,
  footer,
  onDismiss,
  onAuthenticatedClick,
  onContinueAsGuest,
  accentColor,
}: AlertCtaBannerProps) {
  const isOn = alreadyActive || pendingConfirmation;
  const showDismiss = !alreadyActive && !pendingConfirmation;

  const handleToggle = (next: boolean) => {
    if (!next) return; // turning an active/pending alert off happens on the alerts settings page, not here
    if (isAuthenticated) {
      onAuthenticatedClick();
    } else {
      onContinueAsGuest();
    }
  };

  return (
    <div
      className={`flex flex-col gap-3 rounded-[20px] border border-jc-border/60 border-l-4 bg-jc-surface px-4 py-3.5 shadow-sm ${accentColor ? '' : 'border-l-jc-accent'}`}
      style={accentColor ? { borderLeftColor: accentColor } : undefined}
      role="region"
      aria-label={ariaLabel}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <span
            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-white ${accentColor ? '' : 'bg-jc-accent'}`}
            style={accentColor ? { background: accentColor } : undefined}
          >
            <BellRinging className="h-[18px] w-[18px]" weight="fill" aria-hidden />
          </span>
          <div className="text-sm text-jc-ink">
            {alreadyActive ? (
              <p>
                {successText}{' '}
                <Link
                  href="/tools/searches"
                  locale={uiLocale}
                  className={`font-semibold underline ${accentColor ? '' : 'text-jc-accent'}`}
                  style={accentColor ? { color: accentColor } : undefined}
                >
                  {manageLinkLabel}
                </Link>
              </p>
            ) : pendingConfirmation ? (
              <p className="text-amber-700 dark:text-amber-400">{pendingText}</p>
            ) : (
              <>
                <p className="font-medium">{bannerText}</p>
                {subText && <p className="mt-0.5 text-xs text-jc-muted">{subText}</p>}
              </>
            )}
            {failed && <p className="mt-1 text-red-600">{errorText}</p>}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <FilterSwitch checked={isOn} onCheckedChange={handleToggle} disabled={busy || isOn} aria-label={toggleLabel} activeColor={accentColor} />
          {showDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label={dismissLabel}
              className="cursor-pointer rounded-full p-1.5 text-jc-muted transition hover:bg-jc-page focus:outline-none focus-visible:ring-2 focus-visible:ring-jc-cta/50"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {footer && (
        <div className="border-t border-jc-border/60 pt-2.5">{footer}</div>
      )}
    </div>
  );
}
